import Communications from './communications.service';
import queryString from "query-string";
import axios, { CancelTokenSource } from "axios";
import ApiService from './api.service';
import moment from "moment";
import AlertService from "./alert.service";
import AccountService from "./modules/account.service";
import * as yup from "yup";
import LocalStorageService from "../services/local-storage.service";
import mixpanel from "mixpanel-browser";
import { ENV, Misc } from "../constants";
import { FormikErrors } from "formik";
import { AppType } from "@/models/project.model.ts";
import ProjectService from "@/services/modules/project.service.ts";
import PaymentsService from "@/services/modules/payments.service.ts";
import AgentService from "@/services/modules/agent.service.ts";
import { AudioNotificationService } from "@/services/audio-notification.service.ts";
import commonService from '@/services/common.service.ts';

yup.addMethod(yup.mixed, 'atLeastOne', (args) => {
    const { message } = args;
    // @ts-expect-error disabled type error
    return this.test('atLeastOne', message, (list: any) => {
        // If there are 2+ elements after filtering, we know atMostOne must be false.
        return Object.keys(list).filter(item => item).length > 0
    })
});

const parseQueryString = (q: string): any => {
    return queryString.parse(q.replace('?', ''));
}

const validateMixipanelApplicability = () => {
    // Exclude dev mode and localhost
    if (ENV.ENV_MODE === 'dev' || window.location.hostname === 'localhost') {
        return false;
    }
    // Only allow in prod, test, or uat
    if (ENV.ENV_MODE !== 'prod' && ENV.ENV_MODE !== 'test' && ENV.ENV_MODE !== 'uat') {
        return false;
    }
    const currentUser = CommonService._localStorage.getItem(Misc.LOCAL_STORAGE_LOGGED_IN_USER_DATA);
    const formatedUser = currentUser ? JSON.parse(currentUser) : null;
    return !formatedUser?.mixpanelIgnore;
};

const handleMixpanelTrack = (event_name: string, properties: any = {}) => {
    if (validateMixipanelApplicability()) {
        mixpanel.track(event_name, properties);
    }
};

const handleMixpanelReset = () => {
    if (validateMixipanelApplicability()) {
        mixpanel.reset();
    }
}

const handleMixpanelIdentify = (user: any) => {
    if (validateMixipanelApplicability()) {
        const subscriptionDetails = user?.subscription || {
            plan_name: "Free plan",
        }
        mixpanel.identify(user?.id);
        mixpanel.people.set({
            $email: user?.email,
            $name: user?.name,
            ...subscriptionDetails,
        });
    }
}

const extractFormErrors = (errors: any) => {
    const formErrors: any = {};
    for (const field in errors) {
        if (Object.prototype.hasOwnProperty.call(errors, field)) {
            formErrors[field] = errors[field];
        }
    }
    return formErrors;
};

const handleErrors = <T>(
    setErrors: (errors: FormikErrors<T>) => void,
    err: any,
    showGlobalError: boolean = false
) => {
    if (err?.detail?.errors) {
        setErrors(extractFormErrors(err.errors) as FormikErrors<T>);
    }
    if (err?.detail?.error) {
        AlertService.showToast(err?.detail?.error, "error");
    } else if (showGlobalError) {
        AlertService.showToast('Form contains errors, please check once', 'error');
    }
};

const getCancelToken = (): CancelTokenSource => {
    return axios.CancelToken.source();
}

const getFormDataFromJSON = (obj: any, rootName = '', ignoreList = []): FormData => {

    const formData = new FormData();

    const appendFormData = (data: any, root: any) => {
        if (!ignore(root)) {
            root = root || '';
            if (data instanceof File) {
                formData.append(root, data);
            } else if (Array.isArray(data)) {
                for (let i = 0; i < data.length; i++) {
                    appendFormData(data[i], root + '[' + i + ']');
                }
            } else if (typeof data === 'object' && data) {
                for (const key in data) {
                    if (Object.prototype.hasOwnProperty.call(data, key)) {
                        if (root === '') {
                            appendFormData(data[key], key);
                        } else {
                            appendFormData(data[key], root + '.' + key);
                        }
                    }
                }
            } else if (data !== null && typeof data !== 'undefined') {
                formData.append(root, data);
            }
        }
    }

    const ignore = (root: any) => {
        return Array.isArray(ignoreList)
            && ignoreList.some((x: any) => {
                return x === root;
            });
    }

    appendFormData(obj, rootName);

    return formData;
}

const capitalizeFirstLetter = (string: string | undefined) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
}

const transformTimeStamp = (date: Date | string | undefined) => {
    return moment(date).format('DD-MMM-YYYY | hh:mm A');
}

const generateGreeting = () => {
    // Determine the current time of day
    const currentHour = new Date().getHours();
    let greeting = "Hello";

    if (currentHour < 12) {
        greeting = "Good morning,";
    } else if (currentHour < 18) {
        greeting = "Good afternoon,";
    } else {
        greeting = "Good evening,";
    }
    return greeting;
}

const getSandpackJsonFromResponse = (response: string): string => {
    // This will accumulate code for each file as per the streaming protocol
    const files: Record<string, { code: string }> = {};
    response.split('\n').forEach(line => {
        const message = commonService.processStream(line);
        try {
            const eventType = message?.type;
            const eventData = message?.data;
            const path = eventData?.path;

            switch (eventType) {
                case "file_start":
                    if (path) {
                        if (!(path in files)) {
                            files[path] = { code: "" };
                        }
                    }
                    break;
                case "file_delta":
                    if (path && typeof eventData?.delta === "string") {
                        if (!(path in files)) {
                            files[path] = { code: "" };
                        }
                        files[path].code += eventData.delta;
                    }
                    break;
                default:
                // Ignore unknown events
            }
        } catch (e) {
            // Ignore lines that are not valid JSON
            console.error(e);
        }
    });

    return JSON.stringify(files);
};


const getAppType = (path: string): AppType => {
    if (path.includes("streamlit")) return "streamlit";
    if (path.includes("nextjs")) return "nextjs";
    if (path.includes("html")) return "html";
    return "nextjs"; // default to react if no specific type found
}

const getLanguageFromFilename = (filename: string): string => {
    const extMap: Record<string, string> = {
        ts: "typescript",
        js: "javascript",
        jsx: "jsx",
        tsx: "tsx",
        py: "python",
        json: "json",
        md: "markdown",
        html: "html",
        css: "css",
        sh: "shell",
        sql: "sql",
        txt: "plaintext",
        // Additional languages
        java: "java",
        cpp: "cpp",
        c: "c",
        cs: "csharp",
        go: "go",
        rb: "ruby",
        php: "php",
        rs: "rust",
        swift: "swift",
        kt: "kotlin",
        dart: "dart",
        scala: "scala",
        pl: "perl",
        r: "r",
        yml: "yaml",
        yaml: "yaml",
        xml: "xml",
        graphql: "graphql",
        lua: "lua",
        hs: "haskell",
        ex: "elixir",
        elm: "elm",
        vue: "vue",
        svelte: "svelte",
        astro: "astro"
    };
    return extMap[filename.split(".").pop() || ""] || "javascript";
};

const processStream = (chunk: string) => {
    const trimmed = chunk.trim();
    if (!trimmed.startsWith("data:")) return;

    const jsonStr = trimmed.replace(/^data:\s*/, "");
    const message = JSON.parse(jsonStr);
    return message
}

const showUpgradeOrTopupDialog = (message?: string) => {
    Communications.showUpgradeOrTopupDialogSubject.next({
        message: message || "You need more credits to continue using AI features. Please upgrade your plan or add more credits."
    });
};

const CommonService = {
    parseQueryString,
    handleErrors,
    getFormDataFromJSON,
    capitalizeFirstLetter,
    transformTimeStamp,
    handleMixpanelTrack,
    handleMixpanelIdentify,
    handleMixpanelReset,
    generateGreeting,
    getSandpackJsonFromResponse,
    getAppType,
    getLanguageFromFilename,
    processStream,
    showUpgradeOrTopupDialog,
    getCancelToken,

    _api: ApiService,
    _communications: Communications,
    _alert: AlertService,
    _project_service: ProjectService,
    _agent_service: AgentService,
    _payments_Service: PaymentsService,
    _account: AccountService,
    _localStorage: LocalStorageService,
    _audioNotification: AudioNotificationService
}
export default CommonService;
